import { EXPORT_CHART_AS_IMAGE_WIDTH } from '@modules/common/constants';
import { useExportChartAsImage } from '@modules/common/hooks';
import { useGetDashboardAnalysisLinkWithState } from '@modules/dashboard/hooks';
import { useExportQuantifyFeedback } from '@modules/quantify/components/QuantifyAnalysisToolbarV2/hooks/useExportQuantifyFeedback';
import { useExportQuantifyReportDataAsCSV } from '@modules/quantify/hooks';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { useThemeStore } from '@modules/theme/store/useThemeStore';
import {
  CalendarViewMonthRounded,
  ChatRounded,
  SimCardDownloadRounded,
} from '@mui/icons-material';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import { AnalysisViewFragment } from 'generated/graphql';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { mapFromEdgeScope } from 'v2/core/repository/edge-server/analysis/fromEdgeAnalysis';
import { useGetUser } from 'v2/core/services/user';

const trackQuantifyReportEvents = createAnalyticsTracker('quantifyReport');
const trackQuantifyExportImageEvents = createAnalyticsTracker(
  'quantifyExportChartAsImage',
);

interface DashboardQuantifyExportFeedbackActionProps {
  analysis: AnalysisViewFragment;
  analysisLink: string;
}

export const DashboardQuantifyExportFeedbackAction = ({
  analysis,
  analysisLink,
}: DashboardQuantifyExportFeedbackActionProps) => {
  const { mode } = useThemeStore(({ mode }) => ({ mode }));
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const { analysisLink: analysisLinkWithGlobalFilters } =
    useGetDashboardAnalysisLinkWithState(analysis, mode);

  const { exportChartAsImage: exportChartAsImageAPI } = useExportChartAsImage({
    chartTitle: analysis.title,
    chartURLSlug: `report${analysisLinkWithGlobalFilters}`,
    imageSettings: {
      width: EXPORT_CHART_AS_IMAGE_WIDTH,
    },
  });

  const visualisationType = useQuantifyStore(
    ({ query }) => query.visualisationType,
  );
  const openExportFeedbackRecordsModal = useExportQuantifyFeedback({
    view: 'dashboard',
  });

  const [exportAsCSV] = useExportQuantifyReportDataAsCSV();
  const handleExportTableDataAsCSV = () => {
    exportAsCSV();
    trackQuantifyReportEvents({
      eventName: 'Quantify Report - Download CSV',
      name: analysis.title,
      link: `${window.location.origin}/${tenantSlug}${analysisLink}`,
      scope: mapFromEdgeScope(analysis.scope),
      from: window.location.href,
    });
  };

  const exportChartAsImage = () => {
    exportChartAsImageAPI();
    trackQuantifyExportImageEvents({
      eventName: 'Clicked Export As Image',
      chartType: visualisationType,
      isSavedAnalysis: !!analysis.id,
      from: window.location.href,
    });
  };

  return (
    <>
      <Typography mt={1} variant="body2" pl={2} color="text.disabled">
        Export Quantify as
      </Typography>
      <MenuItem onClick={openExportFeedbackRecordsModal}>
        <ListItemIcon>
          <ChatRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Export Feedback</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleExportTableDataAsCSV}>
        <ListItemIcon>
          <CalendarViewMonthRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Export Table as CSV</ListItemText>
      </MenuItem>
      <MenuItem onClick={exportChartAsImage}>
        <ListItemIcon>
          <SimCardDownloadRounded fontSize="small" />
        </ListItemIcon>
        <ListItemText>Export Chart as Image</ListItemText>
      </MenuItem>
    </>
  );
};
